import { ResultWrapper } from '@b2bsoft/kiosk-common-sdk';
import { observable, action, computed, makeObservable } from 'mobx';
import { Platform, ShellClient } from '@b2bsoft/kiosk-shell-sdk';
import { traceContext, TraceContext } from '@b2bsoft/tracecontext';

import LoaderModel from './shared/LoaderModel';
import ConfigurationModel from './shared/ConfigurationModel';
import { CommonModel } from './common/CommonModel';
import PeripheryStatusModel from './shared/PeripheryStatusModel';
import KioskReadyStatusModel from './shared/KioskReadyStatusModel';
import {
    AuthorizationKeys,
    ErrorCodes,
} from '../helpers/constants/Authorization';
import { Brand } from '../helpers/constants/Brands';
import { http, setupAPI } from '../helpers/HttpApi';
import i18n from '../helpers/i18n';
import { AdminModel } from 'models/admin';
import { SnackbarModel } from 'models/shared/SnackbarModel';
import { history } from 'helpers/History';
import { RoutePaths } from 'helpers/constants/RoutePaths';
import { registerSDK } from '../sdk';
import { Sdk } from 'sdk/Sdk';
import { MessageBroker } from 'helpers/constants/MessageBroker';
import { KioskRabbitListener } from 'platform/KioskSdk.Bus';
import { WizardModel, Billpay } from './kiosk';

import { BusMessage } from '../platform/KioskSdk.Bus';
import {
    DeviceProfile,
    ReceiptAttributes,
    KioskInteractionData,
    EmailReceiptRequest,
    KioskData,
    Payment,
} from 'interfaces';
import defaultLogoUrl from 'assets/images/b2b_logo_bw.svg';
import { LogEvents, logger } from 'helpers';
import { defaultThemeSettings } from 'helpers/constants/defaultThemeSettings';
import CollectionModel from './shared/Collection';

export class StoreModel {
    @observable public loader: LoaderModel;
    @observable public configuration: ConfigurationModel;
    @observable public admin: AdminModel;
    @observable public common: CommonModel;
    @observable public collection: CollectionModel;

    @observable public platform: string | null = null;
    @observable public accessToken: string;
    @observable public peripheryStatus: PeripheryStatusModel;
    @observable public kioskReadyStatus: KioskReadyStatusModel;
    @observable public snackbar: SnackbarModel;
    @observable public shellClient: ShellClient;
    @observable public deviceProfile: DeviceProfile;

    public kioskSdkEvents: KioskRabbitListener;

    billpay: Billpay;
    wizard: WizardModel;

    public traceContext: TraceContext;

    public sdk: Sdk;
    /**
     * This is a case when user wants proceed to pay, but he agrees
     * that he will not receive paper receipt
     * @type {boolean}
     * @memberof StoreModel
     */
    @observable public isProceedWithoutPrinter: boolean = false;
    @observable public hideBackToHome: boolean = false;

    @observable public language: string;
    public kioskId?: string;
    public kioskName?: string;

    public accessCode: string;
    public environment: string;
    public locationId?: number;
    public registerName: string;
    public batchId: string;
    public isRegisterEnabled?: boolean;
    public brand: string;
    public receiptAttributes: ReceiptAttributes;
    public processId: string;

    public paymentAttributes: Payment;

    public user: string = 'Chuck';

    private isConfigUpdated: boolean = false;

    /**
     * It's a feature that will allow our QA to manually input response code for all API requests
     * This is needed for better QA experience, to fairly test all cases/responses from API
     * @type {boolean}
     * @memberof StoreModel
     */
    public isQA: boolean = false;

    /**
     *  Parameter which controls if we need to disable inactivity timer
     *  This is actually needed for development to prevent annoying  popups
     * @type {boolean}
     * @memberof StoreModel
     */
    public disableInactivityTimer: boolean = false;

    constructor(loader: LoaderModel, configuration: ConfigurationModel) {
        this.deviceProfile = {
            id: 0,
            enableDeployment: true,
            enableMarketing: false,
            enableMarketingDemo: false,
            name: 'Default',
            type: 'kiosk',
            theme: defaultThemeSettings,
            applications: [],
        };
        this.loader = loader;
        this.configuration = configuration;
        this.admin = new AdminModel(this);
        this.kioskReadyStatus = new KioskReadyStatusModel(this);
        this.peripheryStatus = new PeripheryStatusModel(this);
        this.snackbar = new SnackbarModel();
        this.common = new CommonModel(this);
        this.collection = new CollectionModel(this, this.admin);

        this.billpay = new Billpay(this);
        this.wizard = new WizardModel(this);

        this.kioskSdkEvents = new KioskRabbitListener();

        this.traceContext = traceContext;
        this.shellClient = new ShellClient({
            history,
            routesUpgradeTrigger: [
                RoutePaths.IdleScreen,
                RoutePaths.KioskOutOfOrder,
            ],
            onConnectionStatus: this.peripheryStatus.handleConnectionChange,
            onCashAcceptorChanged: this.peripheryStatus.handleCashAcceptorEvent,
            onMessage: (message) => {
                let logMessage: string = '';
                if (message.EventData?.EventId === 'ConfigUpdated') {
                    this.isConfigUpdated = true;
                    this.tryUpdateProfileConfig(history.location.pathname);
                    logMessage =
                        'Received config update message from message broker';
                } else if (message.isTransactionEvent) {
                    const data = { data: message } as unknown as ResultWrapper<
                        BusMessage<any>
                    >;
                    this.kioskSdkEvents.onTransactionChanged(data);
                    logMessage =
                        'Received transaction changed message from message broker';
                } else {
                    logMessage = 'Received message from message broker';
                }

                logger.logInformation(
                    logMessage,
                    LogEvents.MessageBrokerMessageReceived,
                    message
                );
            },
        });
        this.shellClient.init();
        makeObservable(this);
    }

    @action
    setValue(data: any) {
        Object.assign(this, data);
    }

    @action
    setLang(lang: string) {
        return i18n.changeLanguage(lang).then((_) => {
            this.setValue({ language: lang });
            return _;
        });
    }

    @action
    async reset() {
        this.isProceedWithoutPrinter = false;
        this.hideBackToHome = false;

        const searchParams = new URLSearchParams(window.location.search);
        const language = searchParams.get(AuthorizationKeys.lang) || 'en';

        this.billpay.reset();

        // Reset traceContext traceId
        traceContext.start();

        console.log(`New TraceId: ${this.traceContext.traceId}`);
        this.shellClient.updateTraceId(this.traceContext.traceId);

        await this.setLang(language);
    }

    @action
    initFromShell = async () => {
        const data = await this.shellClient.getKioskData();

        this.kioskId = data.kioskId;
        this.accessCode = data.accessCode || '';
        this.environment = data.environment || '';
        this.registerName = data.registerName || '';
        this.batchId = data.batchId || '';
        this.locationId = data.locationId || undefined;
        this.isRegisterEnabled = data.isRegisterEnabled;
        this.brand = data.brand || '';
        this.kioskName = data.kioskName || '';

        //recreate shell token
        await this.login();
    };

    @action
    initShellValues = async (data: KioskInteractionData) => {
        await this.shellClient.saveKioskData(data);
        await this.initFromShell();
    };

    @action
    clearRegistration = async () => {
        await this.shellClient.clearRegistration();
        await this.initFromShell();
    };

    @action
    hideHomeButton = async () => {
        if (this.hideBackToHome) return;
        this.setValue({ hideBackToHome: true });
    };

    @action
    showHomeButton = async () => {
        if (!this.hideBackToHome) return;
        this.setValue({ hideBackToHome: false });
    };

    async init() {
        await this.initFromShell();
        await this.configuration.init();
        await this.initializeSettings();
        await this.loadDeviceProfile();

        this.peripheryStatus.setCashAcceptorStatus(
            await this.shellClient.cashAcceptor.getStatus()
        );

        this.sdk = registerSDK(this);
    }

    async loadDeviceProfile() {
        const deviceProfile = await this.configuration.getProfile();
        this.setDeviceProfile(deviceProfile);
        this.setApplications();
    }

    async initCommonFlow() {
        if (this.admin.isLoggedIn) {
            this.admin.logout();
        }

        await this.loadKioskData();

        if (!this.isKioskRegistered) {
            history.push({
                ...history.location,
                pathname: RoutePaths.KioskOutOfOrder,
            });
            return;
        }

        if (
            !this.deviceProfile?.enableDeployment ||
            !this.deviceProfile?.applications.length
        ) {
            history.push({
                ...history.location,
                pathname: RoutePaths.KioskOutOfOrder,
            });
            return;
        }

        await this.loadReceiptAttributes();
    }

    @action
    emailReceipt(data: EmailReceiptRequest) {
        return http.post(`/api/transaction/email-receipt`, data);
    }

    @action
    async login() {
        const searchParams = new URLSearchParams(window.location.search);

        this.setValue({
            accessToken: await this.shellClient.getToken(),

            platform:
                searchParams.get(AuthorizationKeys.platform) || Platform.kiosk,
            language: searchParams.get(AuthorizationKeys.lang),
            isQA: searchParams.has(AuthorizationKeys.isQA),
            disableInactivityTimer: searchParams.has(
                AuthorizationKeys.disableInactivityTimer
            ),
        });

        setupAPI(
            {
                accessToken: this.accessToken,
                olrIdentity: this.accessCode,
                isQA: this.isQA,
                host: undefined, //it's always same domain
            },
            this
        );
    }

    async initializeSettings() {
        await this.initializeElastic();
        await this.initializeMessageBus();
    }

    async initializeMessageBus() {
        if (!this.isKioskRegistered) {
            return;
        }

        switch (this.configuration.messageBroker) {
            case MessageBroker.RabbitMq:
                await this.shellClient.initRabbit(
                    this.configuration.rabbit,
                    this.kioskId!
                );
                break;
            case MessageBroker.AzureServiceBus:
                await this.shellClient.initAzureServiceBus(
                    this.configuration.azureBus.connectionString,
                    this.configuration.azureBus.topicName,
                    this.kioskId!
                );
                break;
        }
    }

    @action
    setDeviceProfile = (profile: DeviceProfile | null) => {
        if (!profile) {
            return;
        }
        profile.applications = profile.applications.filter((app) => app.enable);
        this.deviceProfile = profile;
    };

    setApplications = () => {
        this.common.setApplications(this.deviceProfile?.applications);
    };

    async initializeElastic() {
        await this.initializeHealthCheck();
        await this.shellClient.initLogger(
            this.configuration.elastic,
            this.configuration.healthCheck.interval
        );
    }

    async initializeHealthCheck() {
        await this.shellClient.saveKioskData({
            networkAvailabilityUrl:
                this.configuration.healthCheck.networkAvailabilityUrl,
        });
    }

    async loadReceiptAttributes() {
        const response = await http.get<ReceiptAttributes>(
            `/api/locations/${this.locationId}/receipt-attributes`
        );
        this.receiptAttributes = response.data || ({} as ReceiptAttributes);
    }

    @action
    setPaymentAttributes(data: Payment) {
        this.paymentAttributes = data;
    }

    @action
    loadKioskData() {
        return http
            .get<KioskData>(`/api/account/kiosk`)
            .then((response) => this.initShellValues(response.data))
            .catch(async (response) => {
                if (!response || !response.data) {
                    return;
                }

                if (
                    response.data.errors.some(
                        (e: any) =>
                            e.code === ErrorCodes.kioskInvalidRegistration ||
                            e.code === ErrorCodes.kioskNotRegistered
                    )
                ) {
                    await this.clearRegistration();
                }
            });
    }

    @action
    async tryUpdateProfileConfig(pathName: string) {
        const availablePathsForUpdate = [
            RoutePaths.IdleScreen,
            RoutePaths.KioskOutOfOrder,
        ];
        if (
            this.isConfigUpdated &&
            availablePathsForUpdate.includes(pathName)
        ) {
            console.log('Updating device profile');
            await this.loadDeviceProfile();
            this.isConfigUpdated = false;
        }
    }

    @computed
    get hasErrors() {
        return (
            !this.kioskReadyStatus.isKioskReady ||
            this.peripheryStatus.hasErrors
        );
    }

    @computed
    get canProceedWithoutPrinter() {
        return (
            this.kioskReadyStatus.isKioskReady &&
            this.peripheryStatus.isKioskOnline &&
            this.peripheryStatus.isCashAccepterReady
        );
    }

    @computed
    get isOutOfOrder() {
        return (
            this.hasErrors &&
            !(this.canProceedWithoutPrinter && this.isProceedWithoutPrinter)
        );
    }

    @computed
    get isExternalMode() {
        return !!this.accessToken;
    }

    @computed
    get isSuddenLink() {
        return this.brand === Brand.SUDDEN_LINK;
    }

    @computed
    get isOptimum() {
        return this.brand === Brand.OPTIMUM;
    }

    @computed
    get kioskLogo() {
        return this.deviceProfile?.theme?.logo || defaultLogoUrl;
    }

    @computed
    get isKioskRegistered() {
        return (
            !!this.accessCode &&
            !!this.locationId &&
            !!this.kioskId &&
            !!this.registerName
        );
    }

    @computed
    get showIdleMonitor() {
        return (
            !this.disableInactivityTimer &&
            this.configuration?.app?.inactivityTime > 0
        );
    }

    @computed
    get session() {
        return {
            token: this.accessToken,
            traceparent: this.traceContext.traceParentHeader,

            locationId: this.locationId,
            kioskId: this.kioskId,
            kioskName: this.kioskName,
            brand: this.brand,
            language: this.language,
            platform: this.platform,

            messageBroker: this.configuration.messageBroker,

            isQa: this.isQA,
            isWithoutPrinter: this.isProceedWithoutPrinter,
        };
    }
}

export default StoreModel;
